import React, { SyntheticEvent, useContext, useMemo, useState } from 'react';

import { SetlistStatusEnum } from '@/components/SetlistSummaryCard/utils';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import {
	BottomCard,
	BottomCardAction,
	BottomCardForm,
} from '@/components/shared/BottomCard';
import { AsaphButton } from '@/components/shared/Button';
import { classNames } from '@/helpers/classNames';
import { useAuth } from '@/providers/AuthProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { isPlatform } from '@ionic/react';
import { PopoverOrigin } from '@material-ui/core';

import { GenerateSetlistContext } from '../..';
import styles from './ActionContent.module.scss';

const isIOS = isPlatform('ios') && isPlatform('capacitor');

const dropdownMenuOrigins: Record<string, PopoverOrigin> = {
	anchorOrigin: {
		horizontal: 'left',
		vertical: 'top',
	},
	transformOrigin: {
		horizontal: 'left',
		vertical: 'bottom',
	},
};

const ActionContent: React.FC = () => {
	const {
		anchorEl,
		open,
		setlistId,
		isEdit,
		isReorderMode,
		setlist,
		status,
		fromOnboarding,
		setFeedbackSetlistId,
		saveDraftSetlist,
		handleDropdownClose,
		setIsEdit,
		handleDeleteDialogOpen,
		setShareOpen,
		duplicateSetlist,
	} = useContext(GenerateSetlistContext);
	const { isRefetching } = useSetlists();
	const { user } = useAuth();
	const [isAiLimitSheetOpen, setIsAiLimitSheetOpen] = useState(false);
	const [isLiveFeedbackSheetOpen, setIsLiveFeedbackSheetOpen] =
		useState(false);

	const EDIT_PULSE_KEY = 'weeklyPanel_editPulseSeen';
	const EDIT_CLICKED_KEY = 'weeklyPanel_editModeClicked';
	const FEEDBACK_PULSE_KEY = 'weeklyPanel_feedbackPulseSeen';
	const [editPulseSeen, setEditPulseSeen] = useState(
		() => localStorage.getItem(EDIT_PULSE_KEY) === 'true',
	);
	const [feedbackPulseSeen, setFeedbackPulseSeen] = useState(
		() => localStorage.getItem(FEEDBACK_PULSE_KEY) === 'true',
	);
	const markEditPulseSeen = () => {
		if (!editPulseSeen) {
			localStorage.setItem(EDIT_PULSE_KEY, 'true');
			setEditPulseSeen(true);
		}
	};
	const markFeedbackPulseSeen = () => {
		if (!feedbackPulseSeen) {
			localStorage.setItem(FEEDBACK_PULSE_KEY, 'true');
			setFeedbackPulseSeen(true);
		}
	};
	const markEditClicked = () => {
		localStorage.setItem(EDIT_CLICKED_KEY, 'true');
	};

	const commonItems = useMemo(
		() => [
			{
				id: 'edit',
				label: 'Edit Setlist',
				onClick: () => {
					markEditPulseSeen();
					markEditClicked();
					setIsEdit(true);
					handleDropdownClose();
				},
			},
			{
				id: 'duplicate',
				label: 'Create Copy',
				onClick: () => {
					duplicateSetlist();
					handleDropdownClose();
				},
			},
			{
				id: 'delete',
				label: 'Delete Setlist',
				onClick: () => {
					handleDeleteDialogOpen();
					handleDropdownClose();
				},
				classes: { root: styles['red'] },
			},
		],
		[
			handleDeleteDialogOpen,
			handleDropdownClose,
			setIsEdit,
			duplicateSetlist,
		],
	);

	const reports = setlist?.feedback?.teamSongFeedbackReports || [];
	const submissions = setlist?.feedback?.submissions || [];
	const hasSubmission = submissions.some(
		(s) => s.userId === user.id && s.teamSongFeedbackSubmissionIds.length,
	);

	if (status === SetlistStatusEnum.DRAFT) {
		return (
			<div className={styles['root']}>
				<AsaphDropdownMenu
					className={styles['dropdown-menu']}
					open={open}
					anchorEl={anchorEl}
					onClose={handleDropdownClose}
					items={commonItems}
					{...dropdownMenuOrigins}
				/>

				<AsaphButton
					label="Share"
					color="grey"
					onClick={() => setShareOpen(true)}
				/>

				<AsaphButton
					label="Publish"
					color="blue"
					disabled={isRefetching}
					onClick={() => saveDraftSetlist(false)}
				/>
			</div>
		);
	}

	if (status === SetlistStatusEnum.UPCOMING) {
		return (
			<div className={styles['root']}>
				<AsaphDropdownMenu
					className={styles['dropdown-menu']}
					open={open}
					anchorEl={anchorEl}
					onClose={handleDropdownClose}
					items={commonItems.filter((i) => i.id !== 'edit')}
					{...dropdownMenuOrigins}
				/>

				<AsaphButton
					label="Edit"
					color="grey"
					disabled={isRefetching}
					className={!editPulseSeen ? 'flashy' : undefined}
					onClick={() => {
						markEditPulseSeen();
						markEditClicked();
						setIsEdit(true);
					}}
				/>

				<AsaphButton
					label="Share"
					color="blue"
					onClick={() => setShareOpen(true)}
				/>
			</div>
		);
	}

	if (status === SetlistStatusEnum.EDITING) {
		// While reorder mode is active, the Reorder/Done row renders inline
		// inside the scrollable song list (GenerateSetlist.tsx), not in this
		// sticky bottom bar — Regenerate/Save must not be shown, and the
		// bottom bar itself should render nothing extra.
		if (isReorderMode) {
			return null;
		}

		return (
			<>
				<div className={styles['root']}>
					{setlistId && (
						<AsaphDropdownMenu
							className={styles['dropdown-menu']}
							open={open}
							anchorEl={anchorEl}
							onClose={handleDropdownClose}
							items={[
								{
									label: 'Delete Setlist',
									onClick: () => {
										handleDeleteDialogOpen();
										handleDropdownClose();
									},
									classes: { root: styles['red'] },
								},
							]}
							{...dropdownMenuOrigins}
						/>
					)}

					<AsaphButton
						label="Save"
						color="blue"
						disabled={isRefetching}
						onClick={() =>
							saveDraftSetlist(
								false,
								// typeof setlist?.draft === 'boolean'
								// 	? setlist.draft
								// 	: true,
							)
						}
					/>
				</div>
			</>
		);
	}

	if (status === SetlistStatusEnum.LIVE) {
		return (
			<div className={styles['root']}>
				<AsaphDropdownMenu
					className={styles['dropdown-menu']}
					open={open}
					anchorEl={anchorEl}
					onClose={handleDropdownClose}
					items={commonItems}
					{...dropdownMenuOrigins}
				/>

				<AsaphButton
					label="Share"
					color="grey"
					disabled={isRefetching}
					onClick={() => setShareOpen(true)}
				/>

				<AsaphButton
					label="Give feedback"
					color="inactive"
					onClick={() => setIsLiveFeedbackSheetOpen(true)}
				/>
				{isLiveFeedbackSheetOpen && (
					<BottomCard
						title="Service is live"
						onClose={() => setIsLiveFeedbackSheetOpen(false)}
					>
						<BottomCardForm>
							Feedback unlocks once the service is complete. Come
							back then to rate the songs.
						</BottomCardForm>
						<BottomCardAction>
							<AsaphButton
								fullWidth
								color="blue"
								label="OK"
								onClick={() =>
									setIsLiveFeedbackSheetOpen(false)
								}
							/>
						</BottomCardAction>
					</BottomCard>
				)}
			</div>
		);
	}

	if (status === SetlistStatusEnum.COMPLETE) {
		return (
			<div className={styles['root']}>
				<AsaphDropdownMenu
					className={styles['dropdown-menu']}
					open={open}
					anchorEl={anchorEl}
					onClose={handleDropdownClose}
					items={commonItems.filter((i) =>
						reports.length ? i.id !== 'edit' : true,
					)}
					{...dropdownMenuOrigins}
				/>

				{!hasSubmission ? (
					<>
						<AsaphButton
							label="Share"
							color="grey"
							disabled={isRefetching}
							onClick={() => setShareOpen(true)}
						/>

						<AsaphButton
							className={classNames([(fromOnboarding || !feedbackPulseSeen) && 'flashy'])}
							label="Give feedback"
							onClick={() => {
								markFeedbackPulseSeen();
								setlistId && setFeedbackSetlistId(setlistId);
							}}
							color="blue"
						/>
					</>
				) : (
					<AsaphButton
						label="Share"
						color="blue"
						disabled={isRefetching}
						onClick={() => setShareOpen(true)}
					/>
				)}
			</div>
		);
	}

	if (status === SetlistStatusEnum.IN_REVIEW) {
		return (
			<div className={styles['root']}>
				<AsaphDropdownMenu
					className={styles['dropdown-menu']}
					open={open}
					anchorEl={anchorEl}
					onClose={handleDropdownClose}
					items={commonItems.filter((i) =>
						reports.length ? i.id !== 'edit' : true,
					)}
					{...dropdownMenuOrigins}
				/>

				{!hasSubmission ? (
					<>
						<AsaphButton
							label="Share"
							color="grey"
							disabled={isRefetching}
							onClick={() => setShareOpen(true)}
						/>

						<AsaphButton
							className={classNames([(fromOnboarding || !feedbackPulseSeen) && 'flashy'])}
							label="Give feedback"
							onClick={() => {
								markFeedbackPulseSeen();
								setlistId && setFeedbackSetlistId(setlistId);
							}}
							color="blue"
						/>
					</>
				) : (
					<AsaphButton
						label="Share"
						color="blue"
						disabled={isRefetching}
						onClick={() => setShareOpen(true)}
					/>
				)}
			</div>
		);
	}

	return null;
};

export const RegenenerateButton: React.FC<{
	isLoading?: boolean;
	isDisabled?: boolean;
	onClick: (e: SyntheticEvent) => void;
	className?: string;
	label?: string;
}> = ({
	isLoading,
	isDisabled = false,
	onClick,
	className,
	label = 'Generate',
}) => {
	return (
		<AsaphButton
			fullWidth
			isLoading={isLoading}
			disabled={isDisabled}
			color="gradient"
			label={label}
			className={className}
			onClick={onClick}
		/>
	);
};

export default ActionContent;
